import React, { useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import UndoIcon from "@mui/icons-material/Undo";
import FlagIcon from "@mui/icons-material/Flag";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import { saveTeCountRecord } from "../../api/coachCountApi";
import {
  gamesToWin,
  gamesWon,
  isGameOver,
  matchWinnerSide,
} from "../../utils/matchScoreboard";
import "./LiveTrainingCoachChronoPanel.css";
import "./LiveTrainingModeTools.css";

const BEST_OF_OPTIONS = [1, 3, 5, 7];

function SideScore({ player, points, games, winner, disabled, onPoint, t }) {
  return (
    <div className={`lt-match__side${winner ? " lt-match__side--winner" : ""}`}>
      <div className="lt-match__name">
        {winner ? <EmojiEventsIcon fontSize="inherit" /> : null}
        <span>{player?.name || t("liveModeMatchEmptySeat", "Open seat")}</span>
      </div>
      <div className="lt-match__games" aria-label={t("liveModeMatchGames", "Games")}>
        {games}
      </div>
      <div className="lt-match__points-row">
        <button
          type="button"
          className="lt-chrono-panel__btn lt-match__minus"
          onClick={() => onPoint(-1)}
          disabled={disabled || points <= 0}
          aria-label="-1"
        >
          <RemoveIcon fontSize="small" />
        </button>
        <button
          type="button"
          className="lt-match__points"
          onClick={() => onPoint(1)}
          disabled={disabled}
          aria-label={`${player?.name || ""} +1`}
        >
          {points}
          <AddIcon fontSize="inherit" />
        </button>
      </div>
    </div>
  );
}

/**
 * Coach scoreboard for match / challenge blocks: one card per table, tap a
 * score to add a point, end the game, then save games won per player.
 */
export function LiveTrainingMatchScorePanel({
  open,
  onClose,
  scoreboard,
  teId,
  variant = "match",
  t,
  brandPrimary = "#0877BC",
  brandSecondary = "#46528b",
}) {
  const [saveState, setSaveState] = useState({ busy: false, error: null, savedAt: null });
  if (!open || !scoreboard || typeof document === "undefined") return null;

  const { board } = scoreboard;
  const need = gamesToWin(board.bestOf);
  const finished = board.tables.filter((tbl) => matchWinnerSide(tbl, board.bestOf) != null).length;

  const saveResults = async () => {
    setSaveState({ busy: true, error: null, savedAt: null });
    try {
      const label = t("liveModeMatchGamesWonLabel", "Games won");
      for (const table of board.tables) {
        const won = gamesWon(table);
        for (const [side, player] of [table.playerA, table.playerB].entries()) {
          if (!player) continue;
          await saveTeCountRecord(teId, {
            userId: player.userId,
            playerId: player.playerId,
            count: won[side],
            countLabel: label,
          });
        }
      }
      setSaveState({ busy: false, error: null, savedAt: Date.now() });
    } catch (err) {
      setSaveState({ busy: false, error: err?.message || t("error", "Error"), savedAt: null });
    }
  };

  const sheet = (
    <div className="lt-chrono-panel__backdrop" role="presentation" onClick={onClose}>
      <div
        className="lt-chrono-panel lt-match"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-match-title"
        style={{ "--lt-brand": brandPrimary, "--lt-brand-secondary": brandSecondary }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lt-chrono-panel__header">
          <div className="lt-chrono-panel__header-text">
            <p className="lt-chrono-panel__eyebrow">
              {variant === "challenge"
                ? t("liveModeMatchEyebrowChallenge", "Challenge")
                : t("liveModeMatchEyebrow", "Match play")}
            </p>
            <h2 id="lt-match-title">
              <ScoreboardIcon fontSize="small" />
              {t("liveModeMatchOpen", "Scoreboard")}
            </h2>
            <p className="lt-chrono-panel__subtitle">
              {t(
                "liveModeMatchSubtitle",
                "Tap a score to add a point. End the game when it is over; players see their table live."
              )}
            </p>
          </div>
          <button type="button" className="lt-chrono-panel__close" onClick={onClose} aria-label={t("close", "Close")}>
            <CloseIcon fontSize="small" />
          </button>
        </header>

        <div className="lt-chrono-panel__body lt-match__body">
          <div className="lt-match__toolbar">
            <span className="lt-chrono-panel__section-label">{t("liveModeMatchBestOf", "Best of")}</span>
            <div className="lt-match__bestof" role="radiogroup">
              {BEST_OF_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={board.bestOf === n}
                  className={`lt-match__bestof-btn${board.bestOf === n ? " lt-match__bestof-btn--active" : ""}`}
                  onClick={() => scoreboard.setBestOf(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <span className="lt-match__summary">
              {t("liveModeMatchFirstTo", { count: need })}
              {" · "}
              {t("liveModeMatchFinished", {
                done: finished,
                total: board.tables.length,
                defaultValue: "{{done}}/{{total}} finished",
              })}
            </span>
          </div>

          {board.tables.length === 0 ? (
            <p className="lt-chrono-panel__empty">
              {t("liveModeMatchNoTables", "Seat players at tables to start keeping score.")}
            </p>
          ) : (
            <ol className="lt-match__tables">
              {board.tables.map((table) => {
                const winner = matchWinnerSide(table, board.bestOf);
                const won = gamesWon(table);
                const gameOver = isGameOver(table.current);
                return (
                  <li key={table.tableNumber} className={`lt-match__table${winner != null ? " lt-match__table--done" : ""}`}>
                    <div className="lt-match__table-head">
                      <span className="lt-match__table-no">
                        {t("liveTrainingChronoTable", "Table")} {table.tableNumber}
                      </span>
                      <span className="lt-match__sets">
                        {table.sets.map(([a, b], i) => (
                          <span key={i} className="lt-match__set">{a}-{b}</span>
                        ))}
                      </span>
                    </div>
                    <div className="lt-match__duel">
                      <SideScore
                        player={table.playerA}
                        points={table.current[0]}
                        games={won[0]}
                        winner={winner === 0}
                        disabled={winner != null}
                        onPoint={(d) => scoreboard.point(table.tableNumber, 0, d)}
                        t={t}
                      />
                      <span className="lt-match__vs" aria-hidden>
                        vs
                      </span>
                      <SideScore
                        player={table.playerB}
                        points={table.current[1]}
                        games={won[1]}
                        winner={winner === 1}
                        disabled={winner != null}
                        onPoint={(d) => scoreboard.point(table.tableNumber, 1, d)}
                        t={t}
                      />
                    </div>
                    <div className="lt-match__table-actions">
                      <button
                        type="button"
                        className={`lt-chrono-panel__btn${gameOver ? " lt-chrono-panel__btn--start" : ""}`}
                        onClick={() => scoreboard.endGame(table.tableNumber)}
                        disabled={winner != null || table.current[0] === table.current[1]}
                      >
                        <FlagIcon fontSize="small" />
                        {t("liveModeMatchEndGame", "End game")}
                      </button>
                      <button
                        type="button"
                        className="lt-chrono-panel__btn"
                        onClick={() => scoreboard.undoGame(table.tableNumber)}
                        disabled={table.sets.length === 0}
                      >
                        <UndoIcon fontSize="small" />
                        {t("liveModeMatchUndoGame", "Undo game")}
                      </button>
                      <button
                        type="button"
                        className="lt-chrono-panel__btn lt-chrono-panel__btn--reset"
                        onClick={() => scoreboard.resetTable(table.tableNumber)}
                        disabled={table.sets.length === 0 && table.current[0] === 0 && table.current[1] === 0}
                        aria-label={t("liveTrainingChronoReset", "Reset")}
                        title={t("liveTrainingChronoReset", "Reset")}
                      >
                        <RestartAltIcon fontSize="small" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {board.tables.length > 0 ? (
            <div className="lt-match__footer">
              {saveState.error ? (
                <p className="lt-chrono-panel__error" role="alert">{saveState.error}</p>
              ) : null}
              {saveState.savedAt ? (
                <p className="lt-chrono-panel__summary">{t("liveModeMatchSaved", "Results saved")}</p>
              ) : null}
              <button
                type="button"
                className="lt-chrono-panel__btn lt-chrono-panel__btn--start lt-chrono-panel__btn--save"
                onClick={saveResults}
                disabled={saveState.busy}
              >
                <SaveOutlinedIcon fontSize="small" />
                {saveState.busy ? t("saving", "Saving…") : t("liveModeMatchSave", "Save results")}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, getLiveTrainingPortalRoot() || document.body);
}

export default LiveTrainingMatchScorePanel;
